// ═══════════════════════════════════════════════════════════
// ImportCustomersModal — імпорт клієнтів з Excel/CSV
// Завантаження файлу, маппінг колонок, перегляд, дублікати
// ═══════════════════════════════════════════════════════════
import { useState, useMemo, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useT } from '../contexts/I18nContext';
import { apiFetch } from '../services/api';

const FIELDS = [
  { key: 'name', labelKey: 'import.fieldName', required: false },
  { key: 'lastName', labelKey: 'import.fieldLastName', required: false },
  { key: 'firstName', labelKey: 'import.fieldFirstName', required: false },
  { key: 'phone', labelKey: 'import.fieldPhone', required: true },
  { key: 'additionalPhone', labelKey: 'import.fieldAdditionalPhone', required: false },
  { key: 'email', labelKey: 'import.fieldEmail', required: false },
  { key: 'gender', labelKey: 'import.fieldGender', required: false },
  { key: 'birthday', labelKey: 'import.fieldBirthday', required: false },
  { key: 'category', labelKey: 'import.fieldCategory', required: false },
  { key: 'cardNumber', labelKey: 'import.fieldCardNumber', required: false },
  { key: 'note', labelKey: 'import.fieldNote', required: false },
];

// Auto-detect column mapping by header name
const AUTO_MAP = {
  name: ["ім'я", 'имя', 'name', 'піб', 'фіо', 'клієнт', 'клиент', 'client', 'customer'],
  lastName: ['прізвище', 'фамилия', 'last name', 'lastname', 'surname'],
  firstName: ["ім'я", 'имя', 'first name', 'firstname'],
  phone: ['телефон', 'phone', 'тел', 'mobile', 'мобільний', 'номер'],
  additionalPhone: ['дод. телефон', 'доп. телефон', 'additional phone', 'phone2', 'тел2'],
  email: ['email', 'e-mail', 'пошта', 'почта', 'електронна пошта'],
  gender: ['стать', 'пол', 'gender', 'sex', 'гендер'],
  birthday: ['день народження', 'дата народження', 'birthday', 'birth date', 'дата рождения', 'день рождения', 'дн'],
  category: ['категорія', 'категория', 'category', 'група', 'group'],
  cardNumber: ['картка', 'карта', 'card', '№ картки', 'card number'],
  note: ['примітка', 'заметка', 'note', 'comment', 'коментар'],
};

function normalizePhone(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/[^\d+]/g, '');
  // Remove leading + or country code variations
  if (digits.startsWith('+380')) return digits;
  if (digits.startsWith('380') && digits.length >= 12) return '+' + digits;
  if (digits.startsWith('0') && digits.length >= 10) return '+38' + digits;
  return digits;
}

/**
 * Parse birthday string into { birthDay, birthMonth, birthYear }.
 * Supports: DD.MM.YYYY, DD/MM/YYYY, YYYY-MM-DD, DD.MM, Excel serial numbers.
 */
function parseBirthday(raw) {
  if (!raw) return null;
  const s = String(raw).trim();

  // Excel serial number (e.g. 33456)
  if (/^\d{4,5}$/.test(s) && Number(s) > 1000) {
    const d = new Date((Number(s) - 25569) * 86400000);
    if (!isNaN(d.getTime())) return { birthDay: String(d.getDate()), birthMonth: String(d.getMonth() + 1), birthYear: String(d.getFullYear()) };
  }

  // YYYY-MM-DD
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return { birthDay: m[3], birthMonth: m[2], birthYear: m[1] };

  // DD.MM.YYYY or DD/MM/YYYY
  m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) return { birthDay: m[1], birthMonth: m[2], birthYear: m[3] };

  // DD.MM (no year)
  m = s.match(/^(\d{1,2})[./-](\d{1,2})$/);
  if (m) return { birthDay: m[1], birthMonth: m[2], birthYear: '' };

  return null;
}

function autoMapColumns(headers) {
  const mapping = {};
  const lowerHeaders = headers.map(h => (h || '').toLowerCase().trim());

  for (const field of FIELDS) {
    const synonyms = AUTO_MAP[field.key] || [];
    const idx = lowerHeaders.findIndex(h => synonyms.some(s => h.includes(s)));
    if (idx >= 0) mapping[field.key] = idx;
  }

  // If no "name" but has firstName/lastName, keep those
  // If "name" found but it looks like it contains full name, use it
  return mapping;
}

// Sanity cap on imported rows. Above this, browser xlsx parsing
// stays OK but the per-row sequential POST chain takes >10 min,
// duplicate-check chunks balloon, and accidental "test the system"
// 50k-row uploads can spike the day's quotas. 5000 covers >99% of
// real salon migrations (5 staff × 5 years of new customers).
const MAX_IMPORT_ROWS = 5000;

export default function ImportCustomersModal({ onClose, onImport }) {
  const { t } = useT();
  const [step, setStep] = useState('upload'); // upload → map → preview → done
  const [rawData, setRawData] = useState([]); // parsed rows from file
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({}); // { fieldKey: columnIndex }
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState('');
  const [tooManyRows, setTooManyRows] = useState(0); // 0 = OK, >0 = blocked count

  // Existing phones for duplicate detection — checked server-side
  // against ONLY the phones in the imported CSV (vs. pulling the
  // entire tenant customers list). Re-runs when mapping/rawData
  // change; chunks above 1000 phones per batch.
  const [existingPhones, setExistingPhones] = useState(() => new Set());
  useEffect(() => {
    if (!rawData.length || mapping.phone === undefined) {
      setExistingPhones(new Set());
      return;
    }
    const phones = [];
    for (const row of rawData) {
      const p = normalizePhone(String(row[mapping.phone] || '').trim());
      if (p) phones.push(p);
    }
    const unique = [...new Set(phones)];
    if (unique.length === 0) { setExistingPhones(new Set()); return; }

    let cancelled = false;
    const chunks = [];
    for (let i = 0; i < unique.length; i += 1000) chunks.push(unique.slice(i, i + 1000));
    Promise.all(chunks.map(chunk =>
      apiFetch('/api/analytics/customers-by-phones', { method: 'POST', body: { phones: chunk } })
        .then(r => r?.customers || [])
        .catch(() => [])
    )).then(all => {
      if (cancelled) return;
      const set = new Set();
      for (const c of all.flat()) {
        if (c.phoneNormalized) set.add(c.phoneNormalized);
        else if (c.phone) set.add(normalizePhone(c.phone));
      }
      setExistingPhones(set);
    });
    return () => { cancelled = true; };
  }, [rawData, mapping]);

  // Handle file upload
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (json.length < 2) return;

        const hdrs = json[0].map(h => String(h || '').trim());
        const rows = json.slice(1).filter(r => r.some(cell => cell !== ''));
        if (rows.length > MAX_IMPORT_ROWS) {
          setTooManyRows(rows.length);
          setHeaders([]);
          setRawData([]);
          return;
        }
        setTooManyRows(0);
        setHeaders(hdrs);
        setRawData(rows);
        const autoMap = autoMapColumns(hdrs);
        setMapping(autoMap);
        // Skip mapping step if phone was auto-detected
        setStep(autoMap.phone !== undefined ? 'preview' : 'map');
      } catch {
        // invalid file
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Build preview data from mapping
  const previewRows = useMemo(() => {
    if (!rawData.length || mapping.phone === undefined) return [];
    return rawData.map(row => {
      const obj = {};
      for (const f of FIELDS) {
        if (mapping[f.key] !== undefined) {
          obj[f.key] = String(row[mapping[f.key]] || '').trim();
        }
      }
      // Combine firstName + lastName into name if no name field mapped
      if (!obj.name && (obj.firstName || obj.lastName)) {
        obj.name = [obj.lastName, obj.firstName].filter(Boolean).join(' ');
      }
      obj.phone = normalizePhone(obj.phone);
      // Parse gender
      if (obj.gender) {
        const g = obj.gender.toLowerCase().trim();
        if (['ж', 'жін', 'жіноча', 'женский', 'female', 'f', 'ж.'].some(s => g.includes(s))) obj.gender = 'female';
        else if (['ч', 'чол', 'чоловіча', 'мужской', 'male', 'm', 'ч.'].some(s => g.includes(s))) obj.gender = 'male';
        else obj.gender = '';
      }
      // Parse birthday
      if (obj.birthday) {
        const bd = parseBirthday(obj.birthday);
        if (bd) { obj.birthDay = bd.birthDay; obj.birthMonth = bd.birthMonth; obj.birthYear = bd.birthYear; }
        delete obj.birthday;
      }
      obj._duplicate = obj.phone && existingPhones.has(obj.phone);
      return obj;
    }).filter(r => r.phone); // skip rows without phone
  }, [rawData, mapping, existingPhones]);

  const newRows = previewRows.filter(r => !r._duplicate);
  const duplicateCount = previewRows.length - newRows.length;

  // Download template
  const handleDownloadTemplate = () => {
    const header = [t('import.fieldName'), t('import.fieldPhone'), t('import.fieldEmail'), t('import.fieldGender'), t('import.fieldBirthday'), t('import.fieldCategory'), t('import.fieldCardNumber'), t('import.fieldNote')];
    const example = ['Іван Петренко', '+380671234567', 'ivan@example.com', 'Чол', '15.03.1990', '', '', ''];
    const ws = XLSX.utils.aoa_to_sheet([header, example]);
    ws['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 22 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 25 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    XLSX.writeFile(wb, 'import_template.xlsx');
  };

  // Import
  const handleImport = async () => {
    setImporting(true);
    const toImport = newRows.map(r => {
      const { _duplicate, firstName, lastName, ...rest } = r;
      return { ...rest, createdAt: Date.now() };
    });
    try {
      const r = await onImport(toImport);
      // The server's count: it also skips numbers it finds in another form.
      setResult({
        imported: r?.imported ?? toImport.length,
        duplicates: duplicateCount + (r?.skippedDuplicates || 0),
      });
      setStep('done');
    } catch {
      setResult({ error: true });
    }
    setImporting(false);
  };

  return (
    <div className="modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 700, width: '95%' }}>
        <div className="modal-header">
          <h2>{t('import.title')}</h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>{t('import.uploadHint')}</p>
              <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                {t('import.selectFile')}
                <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ display: 'none' }} />
              </label>
              <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Excel (.xlsx, .xls) {t('import.or')} CSV · {t('import.maxRows', { max: MAX_IMPORT_ROWS }) || `максимум ${MAX_IMPORT_ROWS} рядків`}
              </p>
              {tooManyRows > 0 && (
                <div style={{
                  marginTop: '1rem',
                  padding: '12px 16px',
                  background: '#fef2f2',
                  border: '1px solid #fca5a5',
                  borderRadius: 8,
                  color: '#991b1b',
                  fontSize: '0.85rem',
                  textAlign: 'left',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {t('import.tooManyRowsTitle') || 'Файл занадто великий'}
                  </div>
                  <div>
                    {(t('import.tooManyRowsBody') || 'У файлі {count} рядків — максимум за один імпорт {max}. Розбийте файл на менші частини або зверніться до підтримки.')
                      .replace('{count}', tooManyRows)
                      .replace('{max}', MAX_IMPORT_ROWS)}
                  </div>
                </div>
              )}
              <button
                className="btn btn-outline btn-sm"
                style={{ marginTop: '1rem' }}
                onClick={handleDownloadTemplate}
              >
                📥 {t('import.downloadTemplate')}
              </button>
            </div>
          )}

          {/* Step 2: Column mapping */}
          {step === 'map' && (
            <>
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                📎 {fileName} — {rawData.length} {t('import.rows')}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: '1rem' }}>
                {FIELDS.map(f => (
                  <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ minWidth: 120, fontSize: '0.85rem', fontWeight: f.required ? 600 : 400 }}>
                      {t(f.labelKey)}{f.required ? ' *' : ''}
                    </label>
                    <select
                      value={mapping[f.key] ?? ''}
                      onChange={e => setMapping(prev => {
                        const next = { ...prev };
                        if (e.target.value === '') delete next[f.key];
                        else next[f.key] = Number(e.target.value);
                        return next;
                      })}
                      style={{ flex: 1, padding: '4px 8px', fontSize: '0.85rem', borderRadius: 6, border: '1px solid var(--border)' }}
                    >
                      <option value="">— {t('import.skip')} —</option>
                      {headers.map((h, i) => (
                        <option key={i} value={i}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {mapping.phone === undefined && (
                <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  {t('import.phoneRequired')}
                </div>
              )}
            </>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && (
            <>
              <div style={{ display: 'flex', gap: 16, marginBottom: '1rem', fontSize: '0.9rem' }}>
                <span style={{ color: '#22c55e', fontWeight: 600 }}>{t('import.newClients')}: {newRows.length}</span>
                {duplicateCount > 0 && (
                  <span style={{ color: '#f59e0b', fontWeight: 600 }}>{t('import.duplicates')}: {duplicateCount}</span>
                )}
              </div>
              <div style={{ maxHeight: 350, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-body)', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('import.fieldName')}</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('import.fieldPhone')}</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('import.fieldEmail')}</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('import.fieldBirthday')}</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>{t('import.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.slice(0, 100).map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)', opacity: r._duplicate ? 0.5 : 1 }}>
                        <td style={{ padding: '4px 8px' }}>{r.name || '—'}</td>
                        <td style={{ padding: '4px 8px' }}>{r.phone}</td>
                        <td style={{ padding: '4px 8px' }}>{r.email || '—'}</td>
                        <td style={{ padding: '4px 8px' }}>{r.birthDay && r.birthMonth ? `${r.birthDay}.${r.birthMonth}${r.birthYear ? `.${r.birthYear}` : ''}` : '—'}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                          {r._duplicate
                            ? <span style={{ color: '#f59e0b', fontSize: '0.75rem' }}>{t('import.duplicate')}</span>
                            : <span style={{ color: '#22c55e', fontSize: '0.75rem' }}>{t('import.new')}</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewRows.length > 100 && (
                  <p style={{ textAlign: 'center', padding: 8, color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                    {t('import.andMore', { count: previewRows.length - 100 })}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Step 4: Done */}
          {step === 'done' && result && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
              {result.error ? (
                <>
                  <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>❌</div>
                  <p>{t('import.error')}</p>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>✅</div>
                  <p style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {t('import.success', { count: result.imported })}
                  </p>
                  {result.duplicates > 0 && (
                    <p style={{ color: '#f59e0b', fontSize: '0.9rem' }}>
                      {t('import.skippedDuplicates', { count: result.duplicates })}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === 'map' && (
            <>
              <button className="btn btn-secondary" onClick={() => setStep('upload')}>{t('import.back')}</button>
              <button className="btn btn-primary" disabled={mapping.phone === undefined} onClick={() => setStep('preview')}>
                {t('import.preview')} ({previewRows.length})
              </button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button className="btn btn-secondary" onClick={() => setStep('upload')}>{t('import.back')}</button>
              <button className="btn btn-outline btn-sm" onClick={() => setStep('map')}>{t('import.adjustColumns')}</button>
              <button className="btn btn-primary" disabled={importing || newRows.length === 0} onClick={handleImport}>
                {importing ? t('import.importing') : t('import.importBtn', { count: newRows.length })}
              </button>
            </>
          )}
          {step === 'done' && (
            <button className="btn btn-primary" onClick={onClose}>{t('import.close')}</button>
          )}
          {step === 'upload' && (
            <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          )}
        </div>
      </div>
    </div>
  );
}
